/**
 * Pick the proforma invoice a packing list is built from.
 *
 * CREATE ONLY. `packingListUpdateSchema` omits `piId` outright, so on an
 * existing packing list this component must not be rendered at all — the host
 * shows a read-only PI number plus `packingLists.piLocked` instead. Passing
 * `disabled` here freezes the controls but does NOT make re-pointing safe.
 *
 * Columns are limited by what the list endpoint actually returns: PiSummaryDto
 * carries no ship-to and no invoice number, and the server `q` LIKEs
 * `pi_no` + `customer_po_no` only (repositories/proformaInvoices.ts). Legacy's
 * picker showed INV No. + Ship To and searched bill-to/ship-to — that is a
 * server gap, not an omission here, which is why the search hint promises the
 * legacy scope only as far as the API can honour it.
 */
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Search, X } from "lucide-react";
import type { PiSummaryDto } from "@hisense/shared";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Panel } from "@/components/ui/Panel";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/States";
import { useProformaInvoices } from "@/api/hooks";
import { formatDate } from "@/domain/datetime";

export interface PiPickerTableProps {
  /** pi_items.id owner — the host form's `piId`, which stays the truth. */
  value: string | null;
  /** The whole row is handed over so the host can prefill ship-to / customer PO. */
  onSelect: (pi: PiSummaryDto) => void;
  /**
   * Deselect (legacy `togglePISelection`). Optional: a host that treats the PI
   * as mandatory once chosen simply omits it, and no clear affordance renders
   * rather than one that silently does nothing.
   */
  onClear?: () => void;
  /** Freeze every control while the host form is saving or read-only. */
  disabled?: boolean;
}

export function PiPickerTable({ value, onSelect, onClear, disabled = false }: PiPickerTableProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const [q, setQ] = useState("");
  const [page,setPage]=useState(1);
  /**
   * Display cache for the chosen row. `value` is only an id, and the chosen PI
   * can fall off the result page as soon as the clerk types a new search — the
   * summary strip would otherwise blank out while the selection is still live.
   */
  const [picked, setPicked] = useState<PiSummaryDto | null>(null);

  const results = useProformaInvoices({ q: q.trim() || undefined,page,pageSize:25 });

  // pageSize is capped server-side: this is a page of candidates, not all PIs.
  // Typing in the box is how the rest are reached.
  const rows = useMemo(() => results.data?.data ?? [], [results.data]);

  const selected = useMemo<PiSummaryDto | null>(() => {
    if (!value) return null;
    if (picked?.id === value) return picked;
    return rows.find((pi) => pi.id === value) ?? null;
  }, [value, picked, rows]);

  // The host can clear the PI on its own (starting a fresh form does). Drop the
  // stale cache instead of echoing a PI that is no longer chosen.
  useEffect(() => {
    if (!value) setPicked(null);
  }, [value]);

  const toggle = (pi: PiSummaryDto) => {
    if (disabled) return;
    if (pi.id === value) {
      // Re-clicking the chosen row deselects, but only when the host accepts an
      // empty selection. Otherwise it is a no-op, not a half-cleared form.
      if (!onClear) return;
      setPicked(null);
      onClear();
      return;
    }
    setPicked(pi);
    onSelect(pi);
  };

  const clear = () => {
    if (disabled || !onClear) return;
    setPicked(null);
    onClear();
  };

  return (
    <Panel className="p-4">
      <FieldLabel htmlFor={searchId}>{t("packingLists.selectPi")}</FieldLabel>

      {value ? (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge tone="brand">
            <Check className="size-3" aria-hidden="true" />
            <span className="font-mono tabular">{selected?.piNo ?? "—"}</span>
          </Badge>
          {selected?.billToName ? (
            <span className="min-w-0 truncate text-xs text-ink-soft">{selected.billToName}</span>
          ) : null}
          {onClear ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={clear}
              disabled={disabled}
              aria-label={t("purchaseOrders.clearPi")}
            >
              <X className="size-3.5" aria-hidden="true" />
              {t("purchaseOrders.clearPi")}
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="mb-2 text-xs text-ink-faint">{t("packingLists.noPiSelected")}</p>
      )}

      <Input
        id={searchId}
        value={q}
        onChange={(e) => {setQ(e.target.value);setPage(1);}}
        leading={<Search />}
        placeholder={t("packingLists.selectPiHint")}
        autoComplete="off"
        disabled={disabled}
        className="max-w-md"
      />

      {results.isLoading ? (
        <LoadingState />
      ) : results.error ? (
        <div className="mt-2">
          <ErrorState message={t("pi.errorList")} onRetry={() => void results.refetch()} />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-2">
          {/* 251 PIs exist in production, so in practice this is always the
              no-match case; there is no `pi.empty` key to distinguish it. */}
          <EmptyState title={t("common.noMatches")} />
        </div>
      ) : (
        <div className="mt-2" aria-busy={results.isFetching}>
          <TableShell className="max-h-80 overflow-y-auto">
            <thead>
              <tr>
                <Th pin>{t("pi.piNo")}</Th>
                <Th>{t("common.date")}</Th>
                <Th>{t("pi.billTo")}</Th>
                {/* Số hoá đơn và Ship To: hai thứ người dùng đối chiếu khi chọn
                    PI để lập phiếu đóng gói / phiếu giao hàng. */}
                <Th>{t("invoices.invoiceNo")}</Th>
                <Th>{t("common.shipTo")}</Th>
                <Th align="right">{t("common.items")}</Th>
                <Th>{t("pi.poNo")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((pi) => {
                const isSelected = pi.id === value;
                return (
                  <Tr
                    key={pi.id}
                    interactive={!disabled}
                    className={isSelected ? "bg-row-hover" : undefined}
                    onClick={() => toggle(pi)}
                  >
                    <Td pin className={isSelected ? "bg-row-hover" : undefined}>
                      <div className="flex items-center gap-2">
                        {/*
                          The real control: a table row cannot take focus, so the
                          keyboard path lives here. stopPropagation keeps the row
                          handler from firing a second time and undoing the toggle.
                        */}
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          disabled={disabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggle(pi);
                          }}
                          className={
                            "rounded-xs font-mono tabular text-sm font-medium text-brand " +
                            "outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-bright " +
                            "hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-45"
                          }
                        >
                          {pi.piNo}
                        </button>
                        {isSelected ? (
                          <Badge tone="brand">
                            <Check className="size-3" aria-hidden="true" />
                          </Badge>
                        ) : null}
                      </div>
                    </Td>
                    <Td className="font-mono tabular text-xs text-ink-soft">{formatDate(pi.date)}</Td>
                    <Td className="text-ink">{pi.billToName}</Td>
                    <Td className="font-mono tabular text-xs text-ink-soft">
                      {pi.invNo ?? "—"}
                    </Td>
                    {/* `billToContact` chính là ảnh chụp khối Ship To trên PI
                        (xem dto.ts) — khối nhiều dòng, nên hiện dòng đầu và đưa
                        nguyên khối vào tooltip như hai danh sách kia. */}
                    <Td className="max-w-[12rem] text-xs text-ink-soft">
                      <span className="block truncate" title={pi.billToContact ?? undefined}>
                        {pi.billToContact?.split("\n")[0] || "—"}
                      </span>
                    </Td>
                    <Td align="right" className="font-mono tabular text-ink-soft">
                      {pi.itemCount}
                    </Td>
                    <Td className="font-mono tabular text-xs text-ink-soft">
                      {pi.customerPoNo ?? "—"}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </TableShell>
          <p className="mt-1 text-right font-mono tabular text-xs text-ink-faint">
            {rows.length} / {results.data?.total ?? 0}
          </p>
        </div>
      )}
    <div className="flex items-center justify-end gap-2 p-3"><Button variant="secondary" size="sm" disabled={page<=1||results.isFetching} onClick={()=>setPage(p=>p-1)}>{t("pagination.previous")}</Button><span className="font-mono text-xs">{page}</span><Button variant="secondary" size="sm" disabled={page*25>=(results.data?.total??0)||results.isFetching} onClick={()=>setPage(p=>p+1)}>{t("pagination.next")}</Button></div>
    </Panel>
  );
}
